import { useEffect, useRef } from 'react';
import mixpanel from 'mixpanel-browser';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/auth/auth-store';
import {
  ROUTE_TO_MIXPANEL_EVENT,
  type MixpanelEvent,
} from '@/app/lib/sdk-constants';

/**
 * Mixpanel init + identify + per-module-open event tracking.
 *
 * Mirrors Flutter `menu_container_screen.dart:287-385` (one mixpanel
 * track event per menu switch) + the `AppAnalyticsManager` init pattern.
 *
 * Token source: `import.meta.env.VITE_MIXPANEL_TOKEN`. If absent (dev),
 * mixpanel is initialized in disabled mode — `track()` calls become
 * no-ops, the same observable behavior as the previous
 * `src/shared/lib/analytics.ts` stub.
 *
 * `identify()` fires on every user change so Mixpanel can attribute
 * events to the right person. `register()` sets `organizationId` /
 * `practiceId` / `role` as super-properties so every subsequent track
 * call ships with tenant context.
 *
 * Renders nothing.
 */

let initialized = false;

function ensureInit(): boolean {
  if (initialized) return true;
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;
  if (!token) {
    // No token — leave initialized=false so track() calls short-circuit.
    return false;
  }
  mixpanel.init(token, {
    debug: import.meta.env.DEV,
    // Match Flutter's session tracking which fires on app open. The
    // SDK auto-tracks page views via `track_pageview` so we don't
    // double-fire from the route-observer below.
    track_pageview: false,
    persistence: 'localStorage',
  });
  initialized = true;
  return true;
}

export function MixpanelIntegration() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const lastTrackedEventRef = useRef<MixpanelEvent | null>(null);

  // Init + identify on user change.
  useEffect(() => {
    if (!user) return;
    if (!ensureInit()) return;
    mixpanel.identify(user.userId);
    mixpanel.register({
      organizationId: user.organizationId,
      practiceId: user.practiceId,
      role: user.role,
    });
    if (user.email) mixpanel.people.set({ $email: user.email });
  }, [user?.userId, user?.organizationId, user?.practiceId]);

  // Per-module-open tracking. Fires once per longest-prefix match;
  // de-duped by lastTrackedEventRef so multiple sub-route navigations
  // within the same module don't spam events.
  useEffect(() => {
    if (!initialized) return;
    const match = ROUTE_TO_MIXPANEL_EVENT
      .filter((m) => location.pathname.startsWith(m.prefix))
      .sort((a, b) => b.prefix.length - a.prefix.length)[0];
    if (!match) return;
    if (lastTrackedEventRef.current === match.event) return;
    lastTrackedEventRef.current = match.event;
    mixpanel.track(match.event);
  }, [location.pathname]);

  return null;
}
